/**
 * @file slots.ts
 * @description Места для секций вне страниц и служебные страницы (phase19.16
 * Р2; db-design-r2 §4, §4а; SB40, SB103; решение пользователя 2026-10-09):
 * над лентой блога, под лентой и под статьёй. Р2а (db-design-r2a, ответы 2
 * и 3; SB31): над и под списком товаров — одни для /catalog и всех
 * /catalog/<slug>; шаблон страницы товара. «Над/под всем сайтом» — Р3 (SB20).
 *
 * Место хранится служебной страницей `SitePage.kind = SLOT` со своим `slot`
 * и адресом `_slot-<key>`; шаблон — `SitePage.kind = TEMPLATE` с `template`
 * и адресом `_template-<key>`. Публичный `pages/:slug`, sitemap и цель меню
 * служебные страницы отбрасывают (`isServicePageKind`). Ключи совпадают с
 * enum `SiteSlot` / `SiteTemplate` в Prisma (сервер сверяет тестом). Новое
 * место — новая запись здесь и `ADD VALUE` в enum.
 */

/** Ключи мест (enum `SiteSlot`). */
export const SITE_SLOT_KEYS = [
  "BLOG_FEED_ABOVE",
  "BLOG_FEED_BELOW",
  "ARTICLE_BELOW",
  "CATALOG_ABOVE",
  "CATALOG_BELOW",
] as const;
export type SiteSlotKey = (typeof SITE_SLOT_KEYS)[number];

/** Экран конструктора, к которому относится место. */
export type SiteSlotScreen = "blog" | "catalog";

export interface SiteSlotInfo {
  readonly key: SiteSlotKey;
  /** Название места в кабинете («Сайт → Блог», «Сайт → Каталог»). */
  readonly label: string;
  /** Где на сайте покупатель увидит секции места. */
  readonly place: string;
  /** Экран конструктора (структура «Блога» или «Каталога»). */
  readonly screen: SiteSlotScreen;
}

/** Реестр мест в порядке показа. */
export const SITE_SLOTS: readonly SiteSlotInfo[] = [
  { key: "BLOG_FEED_ABOVE", label: "Над лентой блога", place: "страница блога, над списком статей", screen: "blog" },
  { key: "BLOG_FEED_BELOW", label: "Под лентой блога", place: "страница блога, под списком статей", screen: "blog" },
  { key: "ARTICLE_BELOW", label: "Под статьёй", place: "каждая статья блога, после текста", screen: "blog" },
  {
    key: "CATALOG_ABOVE",
    label: "Над списком товаров",
    place: "каталог и все категории, над списком товаров",
    screen: "catalog",
  },
  {
    key: "CATALOG_BELOW",
    label: "Под списком товаров",
    place: "каталог и все категории, под списком товаров",
    screen: "catalog",
  },
];

/** Места одного экрана конструктора. */
export function siteSlotsFor(screen: SiteSlotScreen): SiteSlotInfo[] {
  return SITE_SLOTS.filter((slot) => slot.screen === screen);
}

/**
 * Префикс служебного адреса страницы места: `_slot-blog-feed-above`. Адреса
 * владельца — только `[a-z0-9-]`, поэтому «_» в начале не пересечётся с ними.
 */
export const SITE_SLOT_SLUG_PREFIX = "_slot-";

/** Служебный адрес страницы места. */
export function siteSlotSlug(key: SiteSlotKey): string {
  return `${SITE_SLOT_SLUG_PREFIX}${key.toLowerCase().replace(/_/g, "-")}`;
}

/** Адрес — служебный адрес места (такую страницу не отдаём публично и не принимаем от владельца). */
export function isSlotSlug(slug: string): boolean {
  return slug.startsWith(SITE_SLOT_SLUG_PREFIX);
}

/** Сужение строки до ключа места. */
export function isSiteSlotKey(value: string): value is SiteSlotKey {
  return (SITE_SLOT_KEYS as readonly string[]).includes(value);
}

// ── Шаблоны (Р2а) ──

/** Ключи шаблонов (enum `SiteTemplate`); категория и статья — Р3. */
export const SITE_TEMPLATE_KEYS = ["PRODUCT"] as const;
export type SiteTemplateKey = (typeof SITE_TEMPLATE_KEYS)[number];

/** Префикс служебного адреса страницы-шаблона: `_template-product`. */
export const SITE_TEMPLATE_SLUG_PREFIX = "_template-";

/** Служебный адрес страницы-шаблона. */
export function siteTemplateSlug(key: SiteTemplateKey): string {
  return `${SITE_TEMPLATE_SLUG_PREFIX}${key.toLowerCase().replace(/_/g, "-")}`;
}

/** Адрес — служебный адрес места или шаблона. */
export function isServiceSlug(slug: string): boolean {
  return slug.startsWith(SITE_SLOT_SLUG_PREFIX) || slug.startsWith(SITE_TEMPLATE_SLUG_PREFIX);
}

/** Сужение строки до ключа шаблона. */
export function isSiteTemplateKey(value: string): value is SiteTemplateKey {
  return (SITE_TEMPLATE_KEYS as readonly string[]).includes(value);
}

/**
 * Служебная страница (место SLOT или шаблон TEMPLATE): не публикуется по
 * адресу, не попадает в sitemap, меню и список страниц владельца.
 */
export function isServicePageKind(kind: string): boolean {
  return kind === "SLOT" || kind === "TEMPLATE";
}
